import React, { useEffect, useState } from "react";
import { Text, View } from "../shared/Themed";
import { StyleSheet } from "react-native";
import { ResponseSelectBox } from "../../models/response";
import DropDownPicker, { ItemType } from "react-native-dropdown-picker";
import { ReadOnlyProps } from "../../utils/templates";

interface Props {
  Items:
    | Array<Record<string, string>>
    | Array<string | number>
    | ItemType<string>[];
  required: boolean;
  title: string;
  placeholder: string;
  width: number | string;
  defaultOption?: ResponseSelectBox | string | null;
  onSelectedValue: (event: any) => void;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  zIndex: number;
}

export const SelectBoxControlOrder: React.FC<ReadOnlyProps<Props>> = (
  props: ReadOnlyProps<Props>
) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(props.defaultOption);
  const [items, setItems] = useState(props.Items);

  useEffect(() => {
    if (props.defaultOption) {
      setValue(props.defaultOption);
    } else {
      setValue("");
    }
  }, [props.defaultOption]);

  const handleOpen = () => {
    setOpen(true);
    props.onOpen?.();
  };

  const handleClose = () => {
    setOpen(false);
    props.onClose?.();
  };

  return (
    <View
      style={[
        styles.container,
        { width: props.width },
        // Add extra margin when dropdown is open to create space
        open && {
          zIndex: props.zIndex + 3000,
          marginBottom: Math.min(items.length * 45, 250) + 10, // Dynamic margin based on items
        },
      ]}
    >
      <View style={styles.row}>
        <View style={styles.labelContainer}>
          <Text style={styles.label}>
            {props.title}{" "}
            {!props.required && (
              <Text style={styles.optionalText}>(Optional)</Text>
            )}
          </Text>
        </View>
        <View
          style={[
            styles.dropdownContainer,
            open && { zIndex: props.zIndex + 3001 },
          ]}
        >
          <DropDownPicker
            schema={{
              label: "value",
              value: "key",
            }}
            open={open}
            value={value as string}
            setValue={setValue}
            items={items as ItemType<string>[]}
            setItems={setItems}
            setOpen={setOpen}
            onSelectItem={(val: any) => {
              props.onSelectedValue(val["key"]);
            }}
            placeholder={props.placeholder}
            placeholderStyle={styles.placeholderStyles}
            listMode="SCROLLVIEW"
            style={[
              styles.dropdown,
              { zIndex: open ? props.zIndex + 3002 : props.zIndex },
            ]}
            dropDownContainerStyle={[
              styles.dropdownOverlay,
              {
                zIndex: props.zIndex + 3003,
                // Ensure minimum height for visibility
                minHeight: Math.min(items.length * 45, 250),
              },
            ]}
            showTickIcon={true}
            listItemContainerStyle={styles.listItemContainerStyle}
            listItemLabelStyle={styles.listItemLabelStyle}
            tickIconStyle={styles.tickIconStyle}
            itemSeparator={false}
            disabled={props.disabled}
            onOpen={handleOpen}
            onClose={handleClose}
            // Critical props to prevent cutoff
            maxHeight={250}
            autoScroll={true}
            searchable={false}
            closeAfterSelecting={true}
            dropDownDirection="AUTO"
            bottomOffset={50}
            // Enhanced FlatList props for better performance
            flatListProps={{
              initialNumToRender: 8,
              maxToRenderPerBatch: 8,
              windowSize: 5,
              removeClippedSubviews: false, // Important: Don't clip subviews
              scrollEnabled: true,
              nestedScrollEnabled: true,
              keyboardShouldPersistTaps: "handled",
              getItemLayout: (data, index) => ({
                length: 45,
                offset: 45 * index,
                index,
              }),
            }}
            // Additional props to ensure proper rendering
            translation={{
              PLACEHOLDER: props.placeholder,
              SEARCH_PLACEHOLDER: "Search...",
              SELECTED_ITEMS_COUNT_TEXT: "{count} items selected",
              NOTHING_TO_SHOW: "No options available",
            }}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    marginBottom: 15,
    overflow: "visible",
    minHeight: 50,
    // Ensure container has sufficient space
    paddingBottom: 5,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    overflow: "visible",
    minHeight: 44,
  },
  labelContainer: {
    flex: 1,
    justifyContent: "center",
    minHeight: 44,
    overflow: "visible",
  },
  label: {
    fontSize: 15,
    fontWeight: "400",
    textAlign: "left",
    color: "black",
  },
  optionalText: {
    fontSize: 10,
    color: "lightgrey",
  },
  dropdownContainer: {
    flex: 1.75,
    paddingLeft: 15,
    overflow: "visible",
    minHeight: 30,
  },
  dropdown: {
    borderWidth: 1,
    borderRadius: 4,
    borderColor: "#79747E",
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 8,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
  },
  placeholderStyles: {
    color: "grey",
    fontSize: 14,
  },
  dropdownOverlay: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 2,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: "#79747E",
    backgroundColor: "white",
    // Enhanced shadow for better visibility
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.35,
    shadowRadius: 6,
    elevation: 12, // Much higher elevation for Android
    // Ensure proper sizing
    maxHeight: 250,
    minHeight: 45, // At least one item height
  },
  listItemContainerStyle: {
    display: "flex",
    flexDirection: "row",
    minHeight: 45, // Consistent item height
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: "#E5E5E5",
    backgroundColor: "white", // Ensure white background
  },
  listItemLabelStyle: {
    flex: 1,
    fontSize: 14,
    color: "#333",
    fontWeight: "400",
    paddingRight: 8,
  },
  tickIconStyle: {
    marginTop: 0,
    marginRight: 8,
    color: "rgb(47, 149, 220)",
    width: 18,
    height: 18,
  },
});
